import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Funnel, Check, X, GearSix, SlidersHorizontal } from '@phosphor-icons/react';
import { ruleChipSummary } from './CustomFilterBuilder';

// Board "Custom filters" dropdown.
//
// Read-only picker: lists the project's saved filters as checkboxes. Ticking
// multiple applies them with AND semantics (a task must match every ticked
// filter). Creating/editing lives on Project Settings → Custom Filters, linked
// from the "Manage custom filters" footer action.
//
// Props:
//   projectId
//   filters           — saved filter objects [{ filter_id, name, rules }]
//   appliedFilterIds  — array of currently-applied filter_ids
//   onToggle(id)      — toggle one filter on/off
//   onClearAll()      — clear all applied custom filters
export default function CustomFiltersMenu({ projectId, filters = [], appliedFilterIds = [], onToggle, onClearAll }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const activeCount = appliedFilterIds.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={activeCount > 0 ? 'default' : 'outline'}
          size="sm"
          className={`h-8 rounded-lg text-[12px] ${activeCount > 0 ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
          data-testid="custom-filters-btn"
        >
          <Funnel size={13} className="mr-1" weight={activeCount > 0 ? 'fill' : 'regular'} />
          Custom filters
          {activeCount > 0 && (
            <span className="ml-1.5 bg-white/25 rounded-full min-w-4 h-4 px-1 flex items-center justify-center text-[10px] font-bold">{activeCount}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0 overflow-hidden shadow-2xl border-zinc-200">
        <div className="px-3 py-2.5 border-b border-zinc-100 bg-gradient-to-br from-zinc-50 to-white">
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-500">Saved filters</p>
          <p className="text-[10px] text-zinc-400 mt-0.5">Tick to apply · multiple combine with AND</p>
        </div>
        <div className="p-1 max-h-[320px] overflow-y-auto">
          {filters.length === 0 && (
            <p className="text-center text-[12px] text-slate-400 py-6 px-3">
              No saved filters yet. Use “Manage custom filters” to create one.
            </p>
          )}
          {filters.map(f => {
            const checked = appliedFilterIds.includes(f.filter_id);
            return (
              <button
                key={f.filter_id}
                onClick={() => onToggle(f.filter_id)}
                className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left hover:bg-zinc-50 transition-colors ${checked ? 'bg-blue-50/60' : ''}`}
                data-testid={`custom-filter-${f.filter_id}`}
              >
                <span className={`inline-flex items-center justify-center w-4 h-4 rounded border flex-shrink-0 ${checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-zinc-300'}`}>
                  {checked && <Check size={11} weight="bold" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-slate-800 truncate">{f.name}</span>
                  <span className="block text-[10px] text-slate-400 truncate">{ruleChipSummary(f.rules)}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="border-t border-zinc-100 p-1 bg-zinc-50/60">
          {activeCount > 0 && (
            <button onClick={() => { onClearAll(); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-white text-[13px] text-slate-500 transition-colors" data-testid="custom-filter-clear">
              <X size={13} /> Clear applied filters
            </button>
          )}
          <button
            onClick={() => { setOpen(false); navigate(`/projects/${projectId}/settings#custom-filters`); }}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-white text-[13px] font-medium text-slate-700 transition-colors"
            data-testid="manage-custom-filters"
          >
            <SlidersHorizontal size={13} weight="bold" /> Manage custom filters
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
